import { useEffect, useState } from "react";
import { COVERS_ORIGIN, desktop, isDesktop } from "../desktop/desktop";
import { displayName } from "../emulator/saves";

// Box art lives same-origin under /covers/ (the player is cross-origin
// isolated, so libretro's own server can't be used directly), put there by
// the admin page's re-sync (api/src/covers.rs) with an index from
// display-name keys to files. The
// key is the game's display name, lowercased, so copies under other file
// names still match. The desktop app isn't on that origin: it reads the
// index through Rust and loads the images from the site.

// In development the app's pages come from the Vite server, which proxies
// /covers and isolates the page (so the site's images wouldn't load direct).
const coversBase = isDesktop && !["localhost", "127.0.0.1"].includes(window.location.hostname) ? COVERS_ORIGIN : "";

let index: Promise<Record<string, string>> | null = null;

function loadIndex(): Promise<Record<string, string>> {
  index ??= (
    isDesktop
      ? desktop.coverIndex()
      : fetch("/covers/index.json").then((response) => (response.ok ? response.json() : {}))
  ).catch(() => ({}));
  return index;
}

/** Drops the loaded index, so the next page to need it reads the new one. */
export function forgetCoverIndex() {
  index = null;
}

/** The index as it is now, read again rather than from this page's copy. */
export function freshCoverIndex(): Promise<Record<string, string>> {
  forgetCoverIndex();
  return loadIndex();
}

/** Whether a game has a cover in `covers`, by its display-name key. */
export const hasCover = (name: string, covers: Record<string, string>) => displayName(name).toLowerCase() in covers;

/** The whole index, once loaded; {} until then. */
export function useCoverIndex(): Record<string, string> {
  const [index, setIndex] = useState<Record<string, string>>({});
  useEffect(() => {
    let live = true;
    loadIndex().then((loaded) => live && setIndex(loaded));
    return () => {
      live = false;
    };
  }, []);
  return index;
}

/** The games with a cover first, each group in its given order. */
export function withCoversFirst<T extends { name: string }>(games: T[], index: Record<string, string>): T[] {
  const has = (game: T) => (hasCover(game.name, index) ? 0 : 1);
  return [...games].sort((a, b) => has(a) - has(b));
}

/** The cover's URL for a game file name, or null when there is none. */
export async function coverFor(name: string): Promise<string | null> {
  const file = (await loadIndex())[displayName(name).toLowerCase()];
  return file ? `${coversBase}/covers/${file}` : null;
}
